import React from 'react';

type Point = { label: string; hours: number };

type Props = {
  title: string;
  points: Point[];
  hint?: string;
};

function clamp(n: number, min: number, max: number) {
  return Math.max(min, Math.min(max, n));
}

export function TimelineChart({ title, points, hint }: Props) {
  const w = 520;
  const h = 160;
  const pad = 18;

  const maxHours = Math.max(1, ...points.map((p) => p.hours));

  const xStep = (w - pad * 2) / Math.max(1, points.length - 1);

  const pts = points.map((p, idx) => {
    const x = pad + idx * xStep;
    const y = pad + (1 - p.hours / maxHours) * (h - pad * 2);
    return { x, y, ...p };
  });

  const d = pts
    .map((p, i) => `${i === 0 ? 'M' : 'L'} ${clamp(p.x, 0, w)} ${clamp(p.y, 0, h)}`)
    .join(' ');

  return (
    <div className="lbf-card p-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="text-sm font-extrabold text-white">{title}</div>
          {hint ? <div className="mt-1 text-xs text-zinc-300/80">{hint}</div> : null}
        </div>
        <div className="lbf-badge">Heures</div>
      </div>

      <div className="mt-4">
        <svg
          width="100%"
          height={h}
          viewBox={`0 0 ${w} ${h}`}
          className="block w-full"
          role="img"
          aria-label={title}
        >
          <defs>
            <linearGradient id="lbfLine" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor="rgb(236,72,153)" />
              <stop offset="100%" stopColor="rgb(99,102,241)" />
            </linearGradient>
            <linearGradient id="lbfFill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="rgb(99,102,241)" stopOpacity="0.25" />
              <stop offset="100%" stopColor="rgb(99,102,241)" stopOpacity="0" />
            </linearGradient>
          </defs>

          {/* grid */}
          {[0.25, 0.5, 0.75].map((t) => (
            <line
              key={t}
              x1={pad}
              x2={w - pad}
              y1={pad + t * (h - pad * 2)}
              y2={pad + t * (h - pad * 2)}
              stroke="rgba(255,255,255,.06)"
              strokeWidth="1"
            />
          ))}

          <path
            d={`${d} L ${w - pad} ${h - pad} L ${pad} ${h - pad} Z`}
            fill="url(#lbfFill)"
            opacity="1"
          />
          <path d={d} fill="none" stroke="url(#lbfLine)" strokeWidth="4" strokeLinecap="round" />

          {pts.map((p, idx) => (
            <g key={idx}>
              <circle cx={p.x} cy={p.y} r="5" fill="white" opacity="0.9" />
              <circle cx={p.x} cy={p.y} r="8" fill="rgb(236,72,153)" opacity="0.10" />
              <text x={p.x} y={h - 6} textAnchor="middle" fill="rgba(255,255,255,.7)" fontSize="11">
                {p.label}
              </text>
            </g>
          ))}
        </svg>
      </div>
    </div>
  );
}


